/**
 * Sticky header with show/hide on scroll
 * Automatically hides header when scrolling down and shows when scrolling up or near top
 */

import { loadDeferredImages } from '@/components/helpers/deferred-images'

export class StickyHeader extends HTMLElement {
  //   static HIDDEN_POSITION = 'translateY(-100%)'
  static TRANSITION = 'top 0.3s ease'
  static UI_THRESHOLD = 200
  static SCROLL_THRESHOLD = window.innerHeight / 2
  static UI_VISIBLE_SELECTOR = '[data-ui~="visible"]'
  static UI_SCROLLED_SELECTOR = '[data-scrolled~="true"]'
  static MENU_ITEM_SELECTOR = '[data-header-menu-item]'
  lastScrollY: number
  ticking: boolean
  isVisible: boolean
  isScrolled: boolean
  static VISIBLE_POSITION: string
  topOffset: number
  elementHeight: number
  headerElement: HTMLElement | null
  boundMenuItemEnter: ((e: MouseEvent) => void) | null = null

  constructor() {
    super()

    // State variables
    this.lastScrollY = window.scrollY
    this.ticking = false
    this.isVisible = true
    this.isScrolled = false

    this.style.transform = StickyHeader.VISIBLE_POSITION
    this.style.transition = StickyHeader.TRANSITION
    this.topOffset = parseInt(window.getComputedStyle(this).top.replace('px', ''))
    this.headerElement = this.querySelector('#header')
    this.elementHeight = this.headerElement ? this.headerElement.getBoundingClientRect().height : this.getBoundingClientRect().height
    // Bind scroll handler
    this.handleScroll = this.handleScroll.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.boundMenuItemEnter = this.handleMenuItemEnter.bind(this)
  }

  get hiddenPosition() {
    return `-${this.elementHeight + this.topOffset}px`
  }

  get visiblePosition() {
    return `${this.topOffset}px`
  }

  connectedCallback() {
    this.topOffset = parseInt(window.getComputedStyle(this).top.replace('px', ''))
    this.headerElement = this.querySelector('#header')
    this.elementHeight = this.headerElement ? this.headerElement.getBoundingClientRect().height : this.getBoundingClientRect().height

    window.addEventListener('scroll', this.handleScroll, { passive: true })
    this.addEventListener('mouseleave', this.handleMouseLeave)
    this.addEventListener('mouseover', this.boundMenuItemEnter!)
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll)
    this.removeEventListener('mouseleave', this.handleMouseLeave)
    if (this.boundMenuItemEnter) {
      this.removeEventListener('mouseover', this.boundMenuItemEnter)
    }
  }

  handleMouseLeave() {
    this.clearOpenSubmenu()
  }

  handleMenuItemEnter(e: MouseEvent) {
    const item = (e.target as HTMLElement).closest<HTMLElement>(StickyHeader.MENU_ITEM_SELECTOR)
    if (item) {
      this.setOpenSubmenu(item)
    }
  }

  setOpenSubmenu(menuItem: HTMLElement) {
    if (menuItem.getAttribute('data-submenu-open') === 'true') return
    this.querySelectorAll(StickyHeader.MENU_ITEM_SELECTOR).forEach((el) => {
      el.removeAttribute('data-submenu-open')
    })
    menuItem.setAttribute('data-submenu-open', 'true')
    loadDeferredImages(menuItem)
  }

  clearOpenSubmenu() {
    this.querySelectorAll(StickyHeader.MENU_ITEM_SELECTOR).forEach((el) => {
      el.removeAttribute('data-submenu-open')
    })
  }

  checkForVisibleUI() {
    return this.querySelector(StickyHeader.UI_VISIBLE_SELECTOR)
  }

  updateHeaderHeightVariable(isVisible: boolean) {
    document.documentElement.style.setProperty(
      '--height-header-adapt',
      isVisible ? `${this.elementHeight}px` : '0px'
    )
  }

  showHeader() {
    if (!this.isVisible) {
      this.style.top = this.visiblePosition
      this.isVisible = true
      this.setAttribute('data-visible', 'visible')
      this.updateHeaderHeightVariable(true)
    }
  }

  hideHeader() {
    if (this.isVisible && !this.checkForVisibleUI()) {
      this.style.top = this.hiddenPosition
      this.isVisible = false
      this.removeAttribute('data-visible')
      this.updateHeaderHeightVariable(false)
    }
  }

  registerScroll() {
    this.isScrolled = true
    this.setAttribute('data-scrolled', 'true')
  }

  unregisterScroll() {
    this.isScrolled = false
    this.removeAttribute('data-scrolled')
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const currentScroll = window.scrollY

        if (currentScroll < StickyHeader.SCROLL_THRESHOLD) {
          this.unregisterScroll()
        } else {
          this.registerScroll()
        }

        // Show header if we're at the top or scrolling up
        if (currentScroll < StickyHeader.UI_THRESHOLD || (currentScroll < this.lastScrollY && currentScroll > StickyHeader.UI_THRESHOLD)) {
          this.showHeader()
        } else if (currentScroll > this.lastScrollY && currentScroll > StickyHeader.UI_THRESHOLD) {
        // Hide header if scrolling down past threshold
          this.hideHeader()
        }
        this.lastScrollY = currentScroll
        this.ticking = false
      })

      this.ticking = true
    }
  }
}
